import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  getWorkExperiences, 
  createWorkExperience, 
  updateWorkExperience, 
  deleteWorkExperience 
} from '../../../services/candidate/workExperienceService';

const WorkExperience = ({ profileData, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    title: '',
    location: '',
    locationType: 'On-site',
    startDate: '',
    endDate: '',
    currentlyWorking: false,
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch work experiences on component mount
  useEffect(() => {
    fetchWorkExperiences();
  }, []);

  const fetchWorkExperiences = async () => {
    try {
      setLoading(true);
      const response = await getWorkExperiences();
      
      if (response.success) {
        setExperiences(response.data || []);
      } else {
        toast.error('Failed to load work experiences');
      }
    } catch (error) {
      console.error('Error fetching work experiences:', error);
      toast.error('Error loading work experiences');
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    const today = new Date().toISOString().split('T')[0];

    // Company Name validation
    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    } else if (formData.companyName.length < 2) {
      errors.companyName = 'Company name must be at least 2 characters';
    } else if (formData.companyName.length > 100) {
      errors.companyName = 'Company name must be less than 100 characters';
    }

    // Title validation
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length < 2) {
      errors.title = 'Title must be at least 2 characters';
    } else if (formData.title.length > 200) {
      errors.title = 'Title must be less than 200 characters';
    }

    // Location validation
    if (!formData.location.trim()) {
      errors.location = 'Location is required';
    } else if (formData.location.length > 200) {
      errors.location = 'Location must be less than 200 characters';
    }

    // Location Type validation
    if (!formData.locationType) {
      errors.locationType = 'Location type is required';
    } else if (!['On-site', 'Remote', 'Hybrid'].includes(formData.locationType)) {
      errors.locationType = 'Please select a valid location type';
    }

    // Start Date validation
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    } else if (new Date(formData.startDate) > new Date()) {
      errors.startDate = 'Start date cannot be in the future';
    }

    // End Date validation (only if not currently working)
    if (!formData.currentlyWorking) {
      if (!formData.endDate) {
        errors.endDate = 'End date is required';
      } else if (new Date(formData.endDate) < new Date(formData.startDate)) {
        errors.endDate = 'End date cannot be before start date';
      } else if (new Date(formData.endDate) > new Date()) {
        errors.endDate = 'End date cannot be in the future';
      }
    }

    // Description validation (optional)
    if (formData.description && formData.description.length > 1000) {
      errors.description = 'Description must be less than 1000 characters';
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    // If currentlyWorking is checked, clear endDate
    if (name === 'currentlyWorking' && checked) {
      setFormData(prev => ({ ...prev, endDate: '' }));
      if (formErrors.endDate) {
        setFormErrors(prev => ({ ...prev, endDate: '' }));
      }
    }
  };

  const openModal = (experience = null) => {
    if (experience) {
      setEditingExperience(experience);
      setFormData({
        companyName: experience.companyName || '',
        title: experience.title || '',
        location: experience.location || '',
        locationType: experience.locationType || 'On-site',
        startDate: experience.startDate ? experience.startDate.split('T')[0] : '',
        endDate: experience.endDate ? experience.endDate.split('T')[0] : '',
        currentlyWorking: experience.currentlyWorking || false,
        description: experience.description || ''
      });
    } else {
      setEditingExperience(null);
      setFormData({
        companyName: '',
        title: '',
        location: '',
        locationType: 'On-site',
        startDate: '',
        endDate: '',
        currentlyWorking: false,
        description: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);

    try {
      // Prepare payload
      const payload = {
        companyName: formData.companyName.trim(),
        title: formData.title.trim(),
        location: formData.location.trim(),
        locationType: formData.locationType,
        startDate: formData.startDate,
        endDate: formData.currentlyWorking ? null : formData.endDate,
        currentlyWorking: formData.currentlyWorking,
        description: formData.description.trim() || undefined
      };

      // Remove end date if currently working
      if(formData.currentlyWorking) {
        delete payload.endDate;
      }

      let response;
      if (editingExperience) {
        // Update existing experience
        response = await updateWorkExperience(editingExperience._id, payload);
      } else {
        // Create new experience
        response = await createWorkExperience(payload);
      }

      if (response.success) {
        toast.success(response.message || 'Work experience saved successfully!');
        setIsModalOpen(false);
        fetchWorkExperiences(); // Refresh the list
        
        // Trigger parent update for profile completion
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to save work experience');
      }
    } catch (error) {
      console.error('Error saving work experience:', error);
      //toast.error('Error saving work experience. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    
    try {
      const response = await deleteWorkExperience(id);
      
      if (response.success) {
        toast.success(response.message || 'Work experience deleted successfully!');
        setExperiences(prev => prev.filter(exp => exp._id !== id));
        
        // Trigger parent update for profile completion
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to delete work experience');
      }
    } catch (error) {
      console.error('Error deleting work experience:', error);
      toast.error('Error deleting work experience. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="work-experience">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Manage your work history and professional experience</h3>          
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 btn-primary text-white px-4 py-2 rounded-lg transition-colors"
        >
          <i className="fas fa-plus"></i>
          Add Experience
        </button>
      </div>

      <div className="space-y-4">
        {experiences.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <i className="fas fa-briefcase text-4xl text-gray-400 mb-3"></i>
            <p className="text-gray-600">No work experience added yet</p>
            <p className="text-gray-500 text-sm mt-1">Add your first work experience to showcase your professional journey</p>
          </div>
        ) : (
          experiences.map((experience) => (
            <div key={experience._id} className="border border-gray-200 rounded-lg p-5 hover:border-gray-300 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 text-lg">{experience.title}</h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        experience.locationType === 'Remote' 
                          ? 'bg-blue-100 text-blue-800' 
                          : experience.locationType === 'Hybrid'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {experience.locationType}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-gray-600 mt-1">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-building text-sm text-gray-400"></i>
                      <span>{experience.companyName}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-gray-600 mt-1">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-map-marker-alt text-sm text-gray-400"></i>
                      <span>{experience.location}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-500 text-sm mt-2">
                    <i className="fas fa-calendar text-sm text-gray-400"></i>
                    <span>
                      {new Date(experience.startDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short' 
                      })} -{' '}
                      {experience.currentlyWorking 
                        ? 'Present' 
                        : new Date(experience.endDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short' 
                          })
                      }
                      <span className="mx-2">•</span>
                      {calculateDuration(experience.startDate, experience.currentlyWorking ? null : experience.endDate)}
                    </span>
                  </div>
                  
                  {experience.description && (
                    <p className="text-gray-600 mt-3 text-sm leading-relaxed">{experience.description}</p>
                  )}
                </div>
                
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => openModal(experience)}
                    className="flex items-center gap-2 bg-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                    title="Edit"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(experience._id)}
                    className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">
                  {editingExperience ? 'Edit Work Experience' : 'Add Work Experience'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Retail Sales Manager"
                  />
                  {formErrors.title && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.title}</p>
                  )}
                </div>

                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.companyName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter company name"
                  />
                  {formErrors.companyName && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.companyName}</p>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Mumbai, Maharashtra"
                  />
                  {formErrors.location && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.location}</p>
                  )}
                </div>

                {/* Location Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="locationType"
                    value={formData.locationType}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.locationType ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="On-site">On-site</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                  {formErrors.locationType && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.locationType}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        formErrors.startDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.startDate && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.startDate}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date {!formData.currentlyWorking && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      disabled={formData.currentlyWorking}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        formErrors.endDate ? 'border-red-500' : 'border-gray-300'
                      } ${formData.currentlyWorking ? 'bg-gray-100' : ''}`}
                    />
                    {formErrors.endDate && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.endDate}</p>
                    )}
                  </div>
                </div>

                {/* Currently Working */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="currentlyWorking"
                    name="currentlyWorking"
                    checked={formData.currentlyWorking}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="currentlyWorking" className="ml-2 text-sm text-gray-700">
                    I currently work here
                  </label>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe your role, responsibilities, and achievements..."
                  />
                  <div className="flex justify-between items-center mt-1">
                    {formErrors.description && (
                      <p className="text-sm text-red-500">{formErrors.description}</p>
                    )}
                    <span className={`text-xs ml-auto ${
                      formData.description.length > 1000 ? 'text-red-500' : 'text-gray-500'
                    }`}>
                      {formData.description.length}/1000
                    </span>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 btn-primary text-white py-2 px-4 rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        {editingExperience ? 'Updating...' : 'Adding...'}
                      </>
                    ) : (
                      editingExperience ? 'Update Experience' : 'Add Experience'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to calculate duration
function calculateDuration(startDate, endDate) {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  
  if (months < 0) {
    years--;
    months += 12;
  }
  
  if (years === 0 && months === 0) {
    return 'Less than a month';
  }
  
  const parts = [];
  if (years > 0) {
    parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  }
  if (months > 0) {
    parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
  }
  
  return parts.join(' ');
}

export default WorkExperience;